import { FC, ReactNode, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { alpha, Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Input } from '../common/form/Input';
import { DatePicker } from '../common/form/DatePicker';
import { ShowMoreToggle } from '../common/form/ShowMoreToggle';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import QcsAlert from '../common/basic/QcsAlert';
import { DropOverlay } from '../common/DropOverlay';
import { CopyableBlock } from './CopyableBlock';
import { isExpiringSoon, isKeyExpiryDisabled, isPublicKeyPem, KeyFormValues, MAX_KEY_EXPIRY_YEARS, OPENSSL_COMMANDS } from './integrationUtils';
import { ApiAuthMethod } from '@api/models/enums/ApiAuthMethod';

/** The partner's public key - pasted, dropped or picked as a file. Once it looks valid, it shrinks to one line. */
const PublicKeyField: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<KeyFormValues>();
  const fileRef = useRef<HTMLInputElement>(null);
  //The commands are for the partner, so most admins never need them.
  const [showCommands, setShowCommands] = useState(false);

  //Whether picked or dropped, the file's content just fills the field - validation then checks it is a public key.
  const handleFile = async (file?: File) => {
    if (file) setFieldValue('publicKeyPem', await file.text());
  };

  if (isPublicKeyPem(values.publicKeyPem)) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
        }}
      >
        <CheckCircleIcon color="success" />
        <QcsTypography variant="body2" sx={{ fontWeight: 600 }}>
          {t('integration.apiAccess.key.publicKeyInserted')}
        </QcsTypography>
        <QcsButton variant="text" onClick={() => setFieldValue('publicKeyPem', '')} sx={{ ml: 'auto', textTransform: 'none' }}>
          {t('integration.apiAccess.key.replacePublicKey')}
        </QcsButton>
      </Box>
    );
  }

  return (
    <>
      <DropOverlay onFiles={(files) => handleFile(files[0])} title={t('integration.apiAccess.key.dropPublicKey')} caption={null}>
        <Input
          name="publicKeyPem"
          label={t('integration.apiAccess.key.publicKey')}
          placeholder={t('integration.apiAccess.key.publicKeyPlaceholder')}
          shrinkLabel
          multiline
          rows={5}
          fullWidth
          required
        />
      </DropOverlay>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <QcsButton variant="text" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()} sx={{ textTransform: 'none' }}>
          {t('integration.apiAccess.key.uploadPublicKey')}
        </QcsButton>
        <input
          type="file"
          accept=".pem,.pub,.txt"
          aria-label={t('integration.apiAccess.key.uploadPublicKey')}
          ref={fileRef}
          onChange={({ target }) => {
            handleFile(target.files?.[0]);
            target.value = '';
          }}
          hidden
        />
        <ShowMoreToggle
          expanded={showCommands}
          onToggle={() => setShowCommands(!showCommands)}
          showMoreLabel={t('integration.apiAccess.key.showCommands')}
          showLessLabel={t('integration.apiAccess.key.hideCommands')}
        />
      </Box>
      {showCommands && (
        <>
          <QcsTypography variant="body2">{t('integration.apiAccess.key.partnerInstructions')}</QcsTypography>
          <CopyableBlock text={OPENSSL_COMMANDS} />
        </>
      )}
    </>
  );
};

interface ValidityOptionProps {
  value: string;
  selected: boolean;
  title: string;
  onSelect: () => void;
  children?: ReactNode;
}

/** One of the validity choices; the whole row selects it, and whatever it needs (the date) opens inside it once picked. */
const ValidityOption: FC<ValidityOptionProps> = ({ value, selected, title, onSelect, children }) => (
  <Box
    onClick={onSelect}
    sx={{
      px: 1.5,
      py: 0.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: selected ? 'primary.main' : 'divider',
      cursor: 'pointer',
    }}
  >
    <FormControlLabel value={value} control={<Radio size="small" />} label={title} sx={{ m: 0, width: '100%' }} />
    {children && <Box sx={{ pb: 1.5, pl: 4, display: 'flex', flexDirection: 'column', gap: 0.5 }}>{children}</Box>}
  </Box>
);

/** For a signing key the partner's public key; for both methods the key's validity. */
export const IntegrationKeyFields: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<KeyFormValues>();

  return (
    <>
      {values.authMethod === ApiAuthMethod.SigningKey ? (
        <PublicKeyField />
      ) : (
        <QcsAlert severity="info">{t('integration.apiAccess.key.apiKeyInstructions')}</QcsAlert>
      )}

      {/* An expiry is the default; a key without one takes an explicit choice and shows why it is not recommended. */}
      <RadioGroup
        value={values.keyNeverExpires ? 'never' : 'date'}
        onChange={(_, value) => setFieldValue('keyNeverExpires', value === 'never')}
        sx={{ gap: 1 }}
      >
        <ValidityOption
          value="date"
          selected={!values.keyNeverExpires}
          title={t('integration.apiAccess.key.expiresAt')}
          onSelect={() => setFieldValue('keyNeverExpires', false)}
        >
          {!values.keyNeverExpires && (
            <>
              <Box sx={{ maxWidth: 240 }}>
                <DatePicker name="keyExpiresAt" label={t('integration.apiAccess.key.expiresAt')} shouldDisableDate={isKeyExpiryDisabled} required />
              </Box>
              {isExpiringSoon(values.keyExpiresAt) && <QcsAlert severity="warning">{t('integration.apiAccess.keys.expiresSoon')}</QcsAlert>}
              <QcsTypography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('integration.apiAccess.key.expiryDescription', { maxYears: MAX_KEY_EXPIRY_YEARS })}
              </QcsTypography>
            </>
          )}
        </ValidityOption>
        <ValidityOption
          value="never"
          selected={values.keyNeverExpires}
          title={t('integration.apiAccess.key.neverExpires')}
          onSelect={() => setFieldValue('keyNeverExpires', true)}
        >
          <QcsTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('integration.apiAccess.key.noExpiryWarning')}
          </QcsTypography>
        </ValidityOption>
      </RadioGroup>
    </>
  );
};
